import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type Fee,
  type FeeManagerConfig,
  type FeeManagerFetchInterface,
  type FeeManagerFetchWriteInterface,
  type FeeType,
  type GetFeesResponse,
  type SaveConfigResponse,
  type SaveFeesResponse
} from "@dealer-center/core-feemanager-interface";
import { Locale } from "@dealer-center/core-labels";

const queryKeyBase = "core-feemanager";

// GET hooks

export const useFees = ({
  tenantType,
  tenantId,
  disabled,
  basePath,
  requestParams
}: FeeManagerFetchInterface & { basePath: string }) => {
  return useQuery({
    queryKey: [queryKeyBase, "fees", tenantType, tenantId],
    queryFn: () => getAllFees(basePath, tenantType, tenantId, requestParams),
    enabled: !disabled && !!tenantId,
    retry: 1,
    staleTime: 1000 * 60 // 1 minute
  });
};

export const useVersion = ({ basePath }: { basePath: string }) => {
  return useQuery({
    queryKey: [queryKeyBase, "version"],
    queryFn: () => getVersion(basePath),
    retry: 1,
    staleTime: Infinity
  });
};

export const useGetConfig = ({
  tenantType,
  tenantId,
  disabled,
  basePath,
  requestParams
}: FeeManagerFetchInterface & { basePath: string }) => {
  return useQuery({
    queryKey: [queryKeyBase, "config", tenantType, tenantId],
    queryFn: () => getConfig(basePath, tenantType, tenantId, requestParams),
    enabled: !disabled && !!tenantId,
    retry: 1,
    staleTime: 1000 * 60 // 1 minute
  });
};

export const useSaveConfig = ({
  tenantType,
  tenantId,
  afterSuccess,
  basePath,
  clientId,
  requestParams
}: FeeManagerFetchWriteInterface & { basePath: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      config,
      tenantUser
    }: {
      config: FeeManagerConfig;
      tenantUser: string;
    }) =>
      saveConfig(
        basePath,
        tenantType,
        tenantId,
        config,
        tenantUser,
        clientId,
        requestParams
      ),
    onSuccess: data => {
      if (data.success) {
        queryClient.setQueryData<Record<string, unknown>>(
          [queryKeyBase, "config", tenantType, tenantId],
          data.config as Record<string, unknown>
        );
      }

      if (typeof afterSuccess === "function") {
        afterSuccess(data);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useSaveConfig] mutation failed:", error);
    }
  });
};

export const useSaveFees = ({
  tenantType,
  tenantId,
  afterSuccess,
  basePath,
  clientId,
  requestParams
}: FeeManagerFetchWriteInterface & { basePath: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fees,
      tenantUser,
      vehicleType
    }: {
      fees: Fee[];
      tenantUser: string;
      vehicleType?: FeeType;
    }) =>
      saveAllFees(
        basePath,
        tenantType,
        tenantId,
        fees,
        tenantUser,
        clientId,
        vehicleType,
        requestParams
      ),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "fees", tenantType, tenantId]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(data);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useSaveFees] mutation failed:", error);
    }
  });
};

// API functions

// GET calls spread tenantType/tenantId last so requestParams can't override them.
const buildQuery = (params: Record<string, string>): string => {
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : "";
};

// Builds the POST RequestInit shared by the write endpoints: merges clientId
// into the body (omitted when absent) and mints a fresh X-CoxAuto-Correlation-Id
// per write. extraHeaders lets a caller add endpoint-specific headers (e.g.
// Accept-Language for /fees).
const buildWriteRequest = (
  payload: Record<string, unknown>,
  clientId?: string,
  extraHeaders?: Record<string, string>
): RequestInit => ({
  // clientId rides in the body to our BFF, which re-emits it as the
  // X-Fee-Client-Id header on the outbound Inventory write.
  body: JSON.stringify({ ...payload, ...(clientId && { clientId }) }),
  headers: {
    "Content-Type": "application/json",
    "X-CoxAuto-Correlation-Id": crypto.randomUUID(),
    ...extraHeaders
  },
  method: "POST"
});

const getConfig = async (
  basePath: string,
  tenantType: string,
  tenantId: string,
  requestParams?: Record<string, string>
): Promise<Record<string, unknown>> => {
  const res = await fetch(
    `${basePath}/config${buildQuery({ ...requestParams, tenantType, tenantId })}`,
    { headers: { accept: "application/json" } }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const getVersion = async (basePath: string): Promise<{ version: string }> => {
  const res = await fetch(`${basePath}/version`);
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const getAllFees = async (
  basePath: string,
  tenantType: string,
  tenantId: string,
  requestParams?: Record<string, string>
): Promise<GetFeesResponse> => {
  const locale = Locale.getLocale();
  const res = await fetch(
    `${basePath}/fees${buildQuery({ ...requestParams, tenantType, tenantId })}`,
    {
      headers: {
        "Accept-Language": locale
      }
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const saveConfig = async (
  basePath: string,
  tenantType: string,
  tenantId: string,
  config: FeeManagerConfig,
  tenantUser: string,
  clientId?: string,
  requestParams?: Record<string, string>
): Promise<SaveConfigResponse> => {
  const res = await fetch(
    `${basePath}/config${buildQuery({ ...requestParams })}`,
    buildWriteRequest({ tenantType, tenantId, config, tenantUser }, clientId)
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const saveAllFees = async (
  basePath: string,
  tenantType: string,
  tenantId: string,
  fees: Fee[],
  tenantUser: string,
  clientId?: string,
  vehicleType?: FeeType,
  requestParams?: Record<string, string>
): Promise<SaveFeesResponse> => {
  const res = await fetch(
    `${basePath}/fees${buildQuery({ ...requestParams })}`,
    buildWriteRequest(
      {
        tenantType,
        tenantId,
        fees,
        tenantUser,
        ...(vehicleType && { vehicleType })
      },
      clientId,
      { "Accept-Language": Locale.getLocale() }
    )
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};
